import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useTheme } from '../../../contexts/ThemeContext';
import DarkModeWrapper from '../../../components/DarkModeWrapper';

const HeroSection = () => {
  const { isDark } = useTheme();
  
  // Function to open WhatsApp chat
  const openWhatsAppChat = () => {
    const phoneNumber = "+94712435202";
    window.open(`https://wa.me/${phoneNumber}`, '_blank');
  };

  // Function to handle CV download
  const handleDownloadCV = () => {
    const link = document.createElement('a');
    link.href = '/assets/Lahiru Senavirathna.pdf';
    link.download = 'Lahiru Senavirathna.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  return (
    <DarkModeWrapper sectionName="student-to-engineer">
      <section className={`relative min-h-screen ${isDark ? 'bg-gradient-to-br from-slate-900 to-slate-800' : 'bg-gradient-to-br from-slate-50 via-white to-blue-50'} pt-20`}>
        {!isDark && (
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23e2e8f0%22%20fill-opacity%3D%220.3%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%221%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-40"></div>
        )}
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content Side */}
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium">
                  <Icon name="Sparkles" size={16} />
                  <span>Professional Story</span>
                </div>
                
                <h1 className="text-4xl lg:text-6xl font-bold text-text-primary leading-tight">
                  From Student to
                  <span className="block gradient-text">Full-Stack Engineer</span>
                </h1>
                
                <div className="flex items-center space-x-3 mb-4">
                  <span className="text-lg text-text-secondary">Founder of</span>
                  <span className="text-2xl font-bold gradient-text">CodeShow LapZ</span>
                </div>
                
                <p className="text-xl text-text-secondary leading-relaxed max-w-2xl">
                  A journey of continuous learning, innovation, and turning complex problems into elegant solutions. 
                  Through CodeShow LapZ, I deliver premium software solutions that drive digital transformation 
                  and business growth. Discover the story behind 4+ years of development excellence.
                </p>
              </div>

              {/* Core Philosophy */}
              <div className={`${isDark ? 'bg-slate-800/80 backdrop-blur-sm border-gray-700' : 'bg-white/80 backdrop-blur-sm border-border'} rounded-2xl p-8 border shadow-premium`}>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary to-brand-purple rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon name="Code2" size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-text-primary mb-2">CodeShow LapZ Philosophy</h3>
                    <p className="text-text-secondary text-lg italic">
                      "Where Code Meets Innovation"
                    </p>
                    <p className="text-text-muted mt-2">
                      At CodeShow LapZ, every line of code serves a purpose, every solution is crafted with excellence, 
                      and every project delivers measurable business value. We transform ideas into powerful digital solutions.
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary">4+</div>
                  <div className="text-sm text-text-muted">Years Experience</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-success">50+</div>
                  <div className="text-sm text-text-muted">Projects Completed</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-brand-purple">15+</div>
                  <div className="text-sm text-text-muted">Technologies Mastered</div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  variant="default"
                  iconName="Download"
                  iconPosition="left"
                  className="hover-lift"
                  onClick={handleDownloadCV}
                >
                  Download CV
                </Button>
                <Button
                  variant="outline"
                  iconName="MessageCircle"
                  iconPosition="left"
                  className="hover-lift"
                  onClick={openWhatsAppChat}
                >
                  Let's Connect
                </Button>
              </div>
            </div>

            {/* Image Side */}
            <div className="relative">
              <div className="relative">
                {/* Background Elements */}
                <div className="absolute -top-8 -right-8 w-32 h-32 bg-gradient-to-br from-primary/20 to-brand-purple/20 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-8 -left-8 w-40 h-40 bg-gradient-to-br from-success/20 to-accent/20 rounded-full blur-3xl"></div>
                
                {/* Main Image Container */}
                <div className={`relative ${isDark ? 'bg-slate-800 border-gray-700' : 'bg-white border-border'} rounded-3xl p-8 shadow-xl border`}>
                  <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200">
                    <Image
                      src="/assets/profile.png"
                      alt="Lahiru Senavirathna - Full-Stack Engineer"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  {/* Floating Status Card */}
                  <div className={`absolute -bottom-4 -right-4 ${isDark ? 'bg-slate-800 border-gray-700' : 'bg-white border-border'} rounded-xl p-4 shadow-lg border`}>
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-success rounded-full animate-pulse"></div>
                      <div>
                        <div className="text-sm font-medium text-text-primary">Available</div>
                        <div className="text-xs text-text-muted">For new projects</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating Tech Icons */}
                <div className={`absolute top-4 -left-4 ${isDark ? 'bg-slate-800 border-gray-700' : 'bg-white border-border'} rounded-lg p-3 shadow-lg border`}>
                  <Icon name="Code" size={20} className="text-primary" />
                </div>
                <div className={`absolute top-1/3 -right-6 ${isDark ? 'bg-slate-800 border-gray-700' : 'bg-white border-border'} rounded-lg p-3 shadow-lg border`}>
                  <Icon name="Zap" size={20} className="text-warning" />
                </div>
                <div className={`absolute bottom-1/3 -left-6 ${isDark ? 'bg-slate-800 border-gray-700' : 'bg-white border-border'} rounded-lg p-3 shadow-lg border`}>
                  <Icon name="Rocket" size={20} className="text-success" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </DarkModeWrapper>
  );
};

export default HeroSection;